import React from "react";

import {
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  makeStyles,
} from "@material-ui/core";
import {
  Eye as VisibilityIcon,
  EyeOff as VisibilityOffIcon,
} from "react-feather";
import { get } from "lodash";

import { ChangePasswordFormProps } from "./types";
import { VisualValidator } from "..";

import { fireOnce } from "@ds-developer/frontend";
import { useTealium } from "@hubcapp/context";

const useStyles = makeStyles(theme => ({
  fields: {
    display: "flex",
    flexWrap: "wrap",
    flexDirection: "column",
    "& > *": {
      flexGrow: 1,
      marginBottom: theme.spacing(2),
    },
  },
  submitButton: {
    marginTop: theme.spacing(1),
  },
  cardActions: {
    padding: theme.spacing(2),
  },
  validatorWrapper: {
    width: "100%",
    maxWidth: "300px",
    margin: "0 auto",
  },
}));

const ChangePasswordForm: React.FC<ChangePasswordFormProps> = ({
  errors,
  handleClickShowPassword,
  handleClickShowConfirmPassword,
  hasCapital = false,
  hasNumber = false,
  isLongerThan8Chars = false,
  register,
  showPassword,
  showConfirmPassword,
}) => {
  const classes = useStyles();
  const { trackEvent } = useTealium();

  const handleSetPasswordEvent = () => {
    fireOnce("Change Password|Interaction|Set Password", () => {
      trackEvent({
        eventCategory: "Change Password",
        eventAction: "Interaction",
        eventLabel: "Set Password",
      });
    });
  };

  const handleConfirmPasswordEvent = () => {
    fireOnce("Change Password|Interaction|Confirm Password", () => {
      trackEvent({
        eventCategory: "Change Password",
        eventAction: "Interaction",
        eventLabel: "Confirm Password",
      });
    });
  };

  return (
    <div className={get(classes, "fields")}>
      <TextField
        autoComplete="new-password"
        error={Boolean(errors?.password)}
        fullWidth
        helperText={errors?.password?.message}
        inputRef={register}
        label="Enter your new password"
        name="password"
        type={showPassword ? "text" : "password"}
        variant="outlined"
        onClick={handleSetPasswordEvent}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowPassword}
              >
                {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        FormHelperTextProps={{
          // @ts-ignore
          ["data-test"]: "password-helper-text",
        }}
        inputProps={{
          // @ts-ignore
          ["data-test"]: "password-field",
        }}
      />
      <Grid container justify="center">
        <Typography variant="body1" color="textSecondary">
          Your password must contain:
        </Typography>
      </Grid>

      <div className={classes.validatorWrapper}>
        <Grid container direction="row" justify="center">
          <VisualValidator
            description="Capital"
            mainText="C"
            isValid={hasCapital}
            data-test="validator-capital"
          />
          <VisualValidator
            description="Number"
            mainText="1"
            isValid={hasNumber}
            data-test="validator-number"
          />
          <VisualValidator
            description="8 characters"
            mainText=">8"
            isValid={isLongerThan8Chars}
            data-test="validator-length"
          />
        </Grid>
      </div>

      <TextField
        autoComplete="new-password"
        error={Boolean(errors?.confirmPassword)}
        fullWidth
        helperText={errors?.confirmPassword?.message}
        inputRef={register}
        label="Re-enter your new password"
        name="confirmPassword"
        type={showConfirmPassword ? "text" : "password"}
        variant="outlined"
        onClick={handleConfirmPasswordEvent}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={handleClickShowConfirmPassword}
              >
                {showConfirmPassword ? (
                  <VisibilityIcon />
                ) : (
                  <VisibilityOffIcon />
                )}
              </IconButton>
            </InputAdornment>
          ),
        }}
        FormHelperTextProps={{
          // @ts-ignore
          ["data-test"]: "confirm-password-helper-text",
        }}
        inputProps={{
          // @ts-ignore
          ["data-test"]: "confirm-password-field",
        }}
      />
    </div>
  );
};

export default ChangePasswordForm;
